/**
 * SidebarContent
 * 
 * Shared sidebar content (menu items) used by both MobileDrawer and DesktopSidebar.
 * Contains all navigation links and logout functionality.
 */

import { useMemo } from 'react';
import { Nav } from 'react-bootstrap';
import { NavLink, useLocation } from 'react-router';
import { authController } from '@/utils/authController';
import { useGetProfileQuery } from '@/redux/api/userApi';
import { useSalesTaxEnabled } from '@/hooks/useSalesTaxEnabled';
import { Image } from 'react-bootstrap';
import { FiLogOut } from 'react-icons/fi';
import Logo from '@gearghost/brand-assets/wordmark/dark.svg';
import { getSidebarMenuItems } from './sidebarMenuItems';
import './SidebarContent.scss';

interface SidebarContentProps {
  /** Closes the mobile drawer when a link is followed. */
  onLinkClick?: () => void;
  isCollapsed?: boolean;
}

export const SidebarContent = ({ onLinkClick, isCollapsed = false }: SidebarContentProps) => {
  const { data: user } = useGetProfileQuery();
  const salesTaxEnabled = useSalesTaxEnabled();
  const logout = () => authController.logout();
  const { pathname } = useLocation();

  // Get menu items based on user role
  const menuItemsData = useMemo(
    () => getSidebarMenuItems(user?.role, { salesTaxEnabled }),
    [user?.role, salesTaxEnabled],
  );

  // Memoize menu items with active state
  const menuItems = useMemo(() => {
    return menuItemsData.map((item) => ({
      ...item,
      isActive: pathname === item.link || pathname.startsWith(item.link + '/'),
    }));
  }, [menuItemsData, pathname]);

  // Handle logout
  const handleLogout = () => {
    logout();
    if (onLinkClick) {
      onLinkClick();
    }
  };

  // Handle link click (close mobile drawer)
  const handleLinkClick = () => {
    if (onLinkClick) {
      onLinkClick();
    }
  };

  return (
    <>
      {/* Sidebar Header with Logo */}
      <div className="sidebar-content-header">
        <NavLink className="logo" to="/" onClick={handleLinkClick}>
          <Image src={Logo} alt="" className="img-fluid" />
        </NavLink>
      </div>

      {/* Menu Items */}
      <div className="list-group">
        {menuItems.map((item) => (
          <Nav.Link
            key={item.id}
            as={NavLink}
            to={item.link}
            className={item.isActive ? 'active' : ''}
            onClick={handleLinkClick}
            aria-current={item.isActive ? 'page' : undefined}
            title={isCollapsed ? item.name : undefined}
          >
            {item.icon}
            {!isCollapsed && <span>{item.name}</span>}
          </Nav.Link>
        ))}
        
        {/* Logout Link */}
        <Nav.Link onClick={handleLogout} aria-label="Logout">
          <FiLogOut size={18} />
          {!isCollapsed && <span>Logout</span>}
        </Nav.Link>
      </div>
    </>
  );
};

